import { useEffect, useId, useRef, useState } from 'react';
import { BadgeCheck, Loader2, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { formatoPorcentaje } from '@/lib/utils/formato-precio';
import { tradeService } from '@/features/trade/services/trade-service';
import type { TariffCodeInfo } from '@/features/trade/types/trade.types';
import type { PartidaDeProducto } from '../../types/comercia.types';

/**
 * «Partida arancelaria *» de Agregar producto (maqueta): se busca por código o
 * por nombre del producto —«8415.10 · aire acondicionado»— contra el arancel
 * oficial, y se guarda la partida del catálogo (su id), no un texto: así el
 * arancel de la importación sale del Libro y se actualiza con cada decreto.
 */
export function BuscadorDePartida({
  valor,
  onCambio,
  invalido,
  id,
  enLinea,
}: {
  valor: PartidaDeProducto | null;
  onCambio: (p: PartidaDeProducto | null) => void;
  invalido?: boolean;
  id?: string;
  /**
   * La lista va debajo, dentro del flujo, en vez de flotar: para usarlo en una
   * ventana chica, donde una lista flotante quedaría cortada.
   */
  enLinea?: boolean;
}) {
  const [texto, setTexto] = useState('');
  const [resultados, setResultados] = useState<TariffCodeInfo[]>([]);
  const [cercanas, setCercanas] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const [resaltada, setResaltada] = useState(0);
  const caja = useRef<HTMLDivElement>(null);
  const listaId = useId();

  useEffect(() => {
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, []);

  // Se espera a que deje de escribir: una consulta por letra devolvería
  // resultados de una búsqueda vieja.
  useEffect(() => {
    const q = texto.trim();
    if (q.length < 2) {
      setResultados([]);
      return;
    }
    let vigente = true;
    const t = setTimeout(async () => {
      setBuscando(true);
      try {
        const r = await tradeService.searchTariffCodes(q);
        if (!vigente) return;
        const exactas = r.results ?? [];
        setCercanas(exactas.length === 0 && (r.suggestions ?? []).length > 0);
        setResultados((exactas.length > 0 ? exactas : (r.suggestions ?? [])).slice(0, 12));
        setResaltada(0);
      } catch {
        if (vigente) setResultados([]);
      } finally {
        if (vigente) setBuscando(false);
      }
    }, 280);
    return () => {
      vigente = false;
      clearTimeout(t);
    };
  }, [texto]);

  const elegir = (p: TariffCodeInfo) => {
    onCambio({ id: p.id, codigo: p.code, descripcion: p.description, ga: p.gaRate });
    setTexto('');
    setAbierto(false);
  };

  if (valor) {
    return (
      <div className="flex min-h-12 items-center gap-3 rounded-xl border border-ok-borde bg-ok-suave px-4 py-2.5">
        <BadgeCheck className="h-4 w-4 shrink-0 text-ok" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-mono font-semibold">{valor.codigo}</span>
            <span className="rounded bg-card/70 px-1.5 py-0.5 text-xs font-semibold text-muted-foreground">
              arancel {formatoPorcentaje(valor.ga, { decimalesMaximos: 2 })}
            </span>
          </p>
          {/* Lo que la distingue primero; de dónde cuelga, abajo y en una línea. */}
          <p className="mt-0.5 text-sm leading-snug">{propiaDe(valor.descripcion)}</p>
          {caminoDe(valor.descripcion) && (
            <p className="truncate text-xs text-muted-foreground" title={valor.descripcion}>
              {caminoDe(valor.descripcion)}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => onCambio(null)}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-card hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Elegir otra partida"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    );
  }

  const mostrarLista = abierto && texto.trim().length >= 2 && !buscando;

  return (
    <div ref={caja} className="relative">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          id={id}
          role="combobox"
          aria-expanded={mostrarLista}
          aria-controls={listaId}
          aria-autocomplete="list"
          aria-invalid={invalido || undefined}
          value={texto}
          onChange={(e) => {
            setTexto(e.target.value);
            setAbierto(true);
          }}
          onFocus={() => setAbierto(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setResaltada((i) => Math.min(i + 1, resultados.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setResaltada((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter' && mostrarLista && resultados[resaltada]) {
              e.preventDefault();
              elegir(resultados[resaltada]);
            } else if (e.key === 'Escape') setAbierto(false);
          }}
          placeholder="Código o producto: 8415.10 · aire acondicionado"
          className={cn(
            'h-12 w-full rounded-xl border bg-campo pl-11 pr-10 text-[15px] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            invalido ? 'border-destructive' : 'border-input'
          )}
        />
        {buscando && (
          <Loader2
            className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
            aria-label="Buscando"
          />
        )}
      </div>
      {mostrarLista && (
        <div
          id={listaId}
          role="listbox"
          aria-label="Partidas arancelarias"
          className={cn(
            'mt-1.5 max-h-80 w-full overflow-y-auto rounded-xl border bg-popover p-1',
            enLinea ? 'relative' : 'absolute z-50 shadow-overlay'
          )}
        >
          {resultados.length === 0 ? (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground">
              Ninguna partida con «{texto.trim()}». Prueba con el nombre del producto.
            </p>
          ) : (
            <>
              {cercanas && (
                <p className="px-3 py-1.5 text-xs text-alerta">
                  Ese código no existe: estas son las partidas más cercanas.
                </p>
              )}
              {resultados.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  role="option"
                  aria-selected={i === resaltada}
                  onMouseEnter={() => setResaltada(i)}
                  onClick={() => elegir(p)}
                  className={cn(
                    'flex w-full items-start gap-3 rounded-lg px-3 py-2 text-left',
                    i === resaltada ? 'bg-accent' : 'hover:bg-accent'
                  )}
                >
                  <span className="shrink-0 font-mono text-sm font-semibold text-primary">
                    {p.code}
                  </span>
                  <span className="min-w-0 flex-1">
                    {/*
                      Lo que distingue a la partida va primero: «Tablet que incluya
                      número IMEI» y «Las demás» quedaban al final de cinco renglones
                      iguales y no se podía elegir sin leer todo.
                    */}
                    <span className="block text-sm font-medium leading-snug">
                      {propiaDe(p.description)}
                    </span>
                    {caminoDe(p.description) && (
                      <span className="line-clamp-2 block text-xs text-muted-foreground">
                        {caminoDe(p.description)}
                      </span>
                    )}
                    {p.chapterName && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {p.chapterName}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs font-semibold text-muted-foreground">
                    {formatoPorcentaje(p.gaRate, { decimalesMaximos: 2 })}
                  </span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** La descripción propia de la partida: el último tramo del camino del arancel. */
function propiaDe(descripcion: string): string {
  const tramos = descripcion.split(' > ');
  return tramos[tramos.length - 1].replace(/:$/, '');
}

/** De dónde cuelga, en una línea: «Máquinas automáticas… › Portátiles…». */
function caminoDe(descripcion: string): string {
  return descripcion
    .split(' > ')
    .slice(0, -1)
    .map((t) => t.replace(/:$/, ''))
    .join(' › ');
}
